/**
 * Streak Milestone Auto-Card — premium minimalist design.
 * Fire gradient, clean stats grid, animated feel.
 */

import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Flame, Zap, Calendar } from 'lucide-react'

interface StreakCardProps {
  metadata: {
    streak_days?: number
    habits_count?: number
    total_completions?: number
  }
}

export const StreakCard = memo(function StreakCard({ metadata }: StreakCardProps): React.ReactElement {
  const { t } = useTranslation()
  const days = metadata.streak_days ?? 0
  const habits = metadata.habits_count ?? 0
  const completions = metadata.total_completions ?? 0

  return (
    <div className="rounded-2xl overflow-hidden border border-orange-500/10 bg-gradient-to-br from-orange-500/[0.08] via-transparent to-red-500/[0.06]">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20 flex items-center justify-center">
          <Flame size={22} className="text-orange-400" />
        </div>
        <div>
          <p className="text-[22px] font-display font-bold text-white leading-none">
            {days}
          </p>
          <p className="text-[11px] text-orange-400/80 font-medium mt-0.5">
            {t('feed.auto.streakDays')}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="flex border-t border-white/[0.04]">
        <StatCell icon={<Flame size={13} className="text-orange-400/60" />} value={habits} label={t('feed.auto.habits')} />
        <StatCell icon={<Zap size={13} className="text-orange-400/60" />} value={completions} label={t('feed.auto.completions')} border />
        <StatCell icon={<Calendar size={13} className="text-orange-400/60" />} value={days} label={t('feed.auto.days')} />
      </div>
    </div>
  )
})

function StatCell({ icon, value, label, border }: {
  icon: React.ReactNode; value: number; label: string; border?: boolean
}): React.ReactElement {
  return (
    <div className={`flex-1 flex flex-col items-center gap-1 py-3 ${
      border ? 'border-x border-white/[0.04]' : ''
    }`}>
      {icon}
      <p className="text-[15px] font-bold text-white tabular-nums">{value}</p>
      <p className="text-[9px] text-text-hint/40 uppercase tracking-wider">{label}</p>
    </div>
  )
}
